import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { fadeMotion, motionStyle } from './motion.js';
import { useReducedMotion } from './media.js';
import { usePrimitiveStyles } from './styles.js';

/**
 * Toasts: non-blocking confirmation that something happened, and nothing else.
 *
 * The rule from BUILD_PLAN 9.4 is that a toast is never used for an error that
 * requires action. That is enforced by the shape of this API rather than by
 * remembering it: there is no danger tone, no error code, and no action
 * button. Anything a person has to answer is a Dialog.
 *
 * Voice: an action keeps its name through the whole flow. `Confirm` produces
 * "Confirmed." and `Send` produces "Sent.", so the call site passes the past
 * tense of its own button label.
 */

/**
 * How long a toast stays, in ms.
 *
 * NOT in the design handoff, which specifies the motion budget but never a
 * dwell time. Five seconds is long enough to read two lines in a gym and short
 * enough not to sit over the next action. Flagged for design sign-off, and
 * overridable per toast.
 */
export const TOAST_DURATION_MS = 5000;

export type ToastTone = 'default' | 'success';

export interface ToastOptions {
  /** Past tense of the action that produced it. "Confirmed." */
  readonly title: string;
  readonly body?: string | undefined;
  readonly tone?: ToastTone | undefined;
  readonly durationMs?: number | undefined;
}

export interface ToastProps extends ToastOptions {
  readonly onDismiss?: (() => void) | undefined;
}

export function Toast({
  title,
  body,
  tone = 'default',
  durationMs = TOAST_DURATION_MS,
  onDismiss,
}: ToastProps): ReactNode {
  usePrimitiveStyles();
  const reducedMotion = useReducedMotion();
  const motion = fadeMotion(reducedMotion);

  // The timer depends on the duration only. Held through a ref so that another
  // toast arriving, which re-renders the region and hands this one a fresh
  // callback, does not restart a countdown that is already half spent.
  const latest = useRef(onDismiss);
  useEffect(() => {
    latest.current = onDismiss;
  }, [onDismiss]);

  /**
   * The dwell timer, pausable.
   *
   * It used to be a bare `setTimeout` that nothing could stop, which meant a
   * toast could disappear out from under someone reaching for its dismiss
   * button: they tab toward it, it unmounts mid-reach, and their tab position
   * goes with it. That is also WCAG 2.2.1, which asks for pause, stop or extend
   * on anything auto-updating that holds an interactive control.
   *
   * Hovering or focusing anywhere in the toast holds it. Leaving resumes with
   * the remaining time rather than a fresh full dwell, so a passing cursor does
   * not silently extend it.
   */
  const [paused, setPaused] = useState(false);
  const remaining = useRef(durationMs);
  const startedAt = useRef<number | null>(null);

  useEffect(() => {
    if (paused) {
      if (startedAt.current !== null) {
        remaining.current = Math.max(
          0,
          remaining.current - (performance.now() - startedAt.current),
        );
        startedAt.current = null;
      }
      return undefined;
    }

    startedAt.current = performance.now();
    const timer = setTimeout(() => {
      latest.current?.();
    }, remaining.current);
    return () => {
      clearTimeout(timer);
    };
  }, [paused, durationMs]);

  return (
    <div
      className="gs-ui gs-toast"
      data-tone={tone}
      data-motion={motion.name}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      style={motionStyle(motion)}
    >
      {/* Color is never the only carrier: the dot marks it, the words say it. */}
      <span className="gs-toast__marker" aria-hidden="true" />
      <span className="gs-toast__text">
        <span className="gs-toast__title">{title}</span>
        {body === undefined ? null : <span className="gs-toast__body">{body}</span>}
      </span>
      {onDismiss === undefined ? null : (
        <button
          type="button"
          className="gs-toast__dismiss"
          aria-label="Dismiss"
          onClick={onDismiss}
        >
          {'×'}
        </button>
      )}
    </div>
  );
}

export interface ToastApi {
  /** Show a toast. Returns its id, so a long-running one can be dismissed early. */
  readonly toast: (options: ToastOptions) => string;
  readonly dismiss: (id: string) => void;
}

interface ToastRecord extends ToastOptions {
  readonly id: string;
}

const ToastContext = createContext<ToastApi | null>(null);

let sequence = 0;
function nextId(): string {
  sequence += 1;
  return `gs-toast-${String(sequence)}`;
}

export interface ToastProviderProps {
  readonly children?: ReactNode | undefined;
}

export function ToastProvider({ children }: ToastProviderProps): ReactNode {
  usePrimitiveStyles();
  const [toasts, setToasts] = useState<readonly ToastRecord[]>([]);

  const dismiss = useCallback((id: string): void => {
    setToasts((current) => current.filter((record) => record.id !== id));
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      toast: (options) => {
        const id = nextId();
        const record: ToastRecord = { ...options, id };
        // An identical toast already showing is replaced in place, under a new
        // id so it remounts and its dwell timer starts again. Quick repeated
        // saves then read as one toast, not a growing stack.
        setToasts((current) =>
          current.some((shown) => shown.title === options.title && shown.body === options.body)
            ? current.map((shown) =>
                shown.title === options.title && shown.body === options.body ? record : shown,
              )
            : [...current, record],
        );
        return id;
      },
      dismiss,
    }),
    [dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/*
        A live region that is always present, so a toast inserted into it is
        announced. A region created at the same moment as its text is not.
      */}
      <div
        className="gs-ui gs-toast-region"
        role="status"
        aria-live="polite"
        // Explicit, because `role="status"` carries an implicit
        // `aria-atomic="true"` and that is exactly wrong for a stack. Atomic
        // means "re-read the whole region", so a third toast re-announces the
        // first two: "Confirmed. Recorded, unattributed. 3 waiting. Saved on
        // this device." To a scorekeeper on VoiceOver, mid-quarter, in a gym.
        aria-atomic="false"
        aria-label="Notifications"
      >
        {toasts.map((record) => (
          <ToastItem key={record.id} record={record} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({
  record,
  onDismiss,
}: {
  readonly record: ToastRecord;
  readonly onDismiss: (id: string) => void;
}): ReactNode {
  const dismiss = useCallback(() => {
    onDismiss(record.id);
  }, [onDismiss, record.id]);

  return (
    <Toast
      title={record.title}
      body={record.body}
      tone={record.tone}
      durationMs={record.durationMs}
      onDismiss={dismiss}
    />
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (api === null) {
    throw new Error('useToast() needs a <ToastProvider> above it. Mount one at the app root.');
  }
  return api;
}
